import { Button, Stack, SelectChangeEvent, useTheme } from '@mui/material';
import {
  LEGACY_ONLY_SCANNERS,
  SG_SPECIAL_SCANNERS,
  SG_STOCK_SCANNERS,
} from 'config';
import { useMemo, useCallback } from 'react';
import { ScannerID, ScannerListItem } from 'types/equityhub';
import OptionsDropdownSelector from 'components/shared/OptionsDropdownSelector';

interface CanvasScannersSectionProps {
  activeScanner: ScannerID | undefined;
  onScannerChange: (scanner: ScannerID | undefined) => void;
}

const CanvasScannersSection = ({
  activeScanner,
  onScannerChange,
}: CanvasScannersSectionProps) => {
  const theme = useTheme();

  // Canvas is forced to SynthOI — always filter out legacy-only scanners
  const sgScanners = useMemo(() => {
    return SG_SPECIAL_SCANNERS.filter(
      (s) => !LEGACY_ONLY_SCANNERS.includes(s.scanner),
    );
  }, []);

  const stockScanners = useMemo(() => {
    return SG_STOCK_SCANNERS.filter(
      (s) => !LEGACY_ONLY_SCANNERS.includes(s.scanner),
    );
  }, []);

  const onScannerSelectChange = useCallback(
    (event: SelectChangeEvent<string | string[]>) => {
      const selectedScanner = event.target.value as ScannerID;
      if (activeScanner === selectedScanner) {
        onScannerChange(undefined);
      } else {
        onScannerChange(selectedScanner);
      }
    },
    [onScannerChange, activeScanner],
  );

  const getScannerValue = useCallback(
    (scannerList: ScannerListItem[]) => {
      const isActive = scannerList.some(
        (scanner) => scanner.scanner === activeScanner,
      );
      return isActive ? (activeScanner ?? '') : '';
    },
    [activeScanner],
  );

  const createScannerOptions = useCallback((scannerList: ScannerListItem[]) => {
    return scannerList.map((scanner) => ({
      label: scanner.label.name,
      value: scanner.scanner,
      icon: scanner.label.icon,
    }));
  }, []);

  return (
    <Stack
      sx={{
        flexDirection: 'row',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <OptionsDropdownSelector
        label="SG Scanners"
        value={getScannerValue(sgScanners)}
        options={createScannerOptions(sgScanners)}
        isMultiple={false}
        onChange={onScannerSelectChange}
        sx={{ minWidth: '160px' }}
      />
      <OptionsDropdownSelector
        label="Stock Scanners"
        value={getScannerValue(stockScanners)}
        options={createScannerOptions(stockScanners)}
        isMultiple={false}
        onChange={onScannerSelectChange}
        sx={{ minWidth: '160px' }}
      />
      <Button
        size="small"
        sx={{
          textTransform: 'capitalize',
          fontSize: 13,
          color: theme.palette.text.primary,
          minWidth: 'fit-content',
        }}
        onClick={() => onScannerChange(undefined)}
      >
        All Symbols
      </Button>
    </Stack>
  );
};

export default CanvasScannersSection;
